import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PRODUCTION_STEPS } from "@produce/shared";
import {
  Edit,
  Calendar,
  Music,
  Upload,
  Users,
  ClipboardList,
  Map,
  FileText,
  Radio,
  CheckCircle,
  ExternalLink,
  X,
} from "lucide-react";

// Icons only. The labels — and their order, which IS the step index this menu
// navigates by — come from PRODUCTION_STEPS, so a step inserted in the wizard
// cannot leave this menu pointing at the wrong screen.
const STEP_ICONS = [
  Calendar,
  Music,
  Upload,
  Users,
  Map,
  ClipboardList,
  FileText,
  Radio,
  CheckCircle,
];

const EDIT_STEPS = PRODUCTION_STEPS.map((label, step) => ({
  step,
  label,
  // Falls back rather than indexing off the end: a tenth wizard step would
  // otherwise render `<undefined />`, which React throws on — taking the whole
  // production page down over a missing icon.
  icon: STEP_ICONS[step] ?? Edit,
}));

interface EditProductionFABProps {
  briefingId: string;
}

export function EditProductionFAB({ briefingId }: EditProductionFABProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div ref={menuRef} className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
      {isOpen && (
        <div className="mb-2 w-64 overflow-hidden rounded-xl border border-border bg-card shadow-2xl duration-200 animate-in fade-in slide-in-from-bottom-4">
          <div className="border-b border-border px-4 py-3">
            <p className="text-sm font-semibold text-foreground">Quick edit</p>
          </div>
          <div className="max-h-[400px] overflow-y-auto py-1">
            {EDIT_STEPS.map(({ step, label, icon: Icon }) => (
              <button
                key={step}
                onClick={() => {
                  navigate(`/briefings/${briefingId}/edit?step=${step}&shortcut=true`);
                  setIsOpen(false);
                }}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-muted-foreground transition-colors hover:bg-accent/10 hover:text-foreground"
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                {label}
              </button>
            ))}
          </div>
          <div className="border-t border-border p-2">
            <button
              onClick={() => {
                navigate(`/briefings/${briefingId}/edit`);
                setIsOpen(false);
              }}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent/10"
            >
              <ExternalLink className="h-4 w-4 flex-shrink-0" />
              Open full editor
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-all hover:scale-105 hover:shadow-xl active:scale-95"
        title="Edit production"
      >
        {isOpen ? <X className="h-6 w-6" /> : <Edit className="h-6 w-6" />}
      </button>
    </div>
  );
}
